import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNotificationStore, AppNotification, NotificationType } from '../../store/notificationStore';
import { LightningIcon, BellIcon, TrophyIcon } from '../icons/WeatherIcons';
import './NotificationCenter.css';

// ── Icons per type ──────────────────────────────────────────────
const TYPE_ICON: Record<NotificationType, React.ReactNode> = {
  trade_executed: <LightningIcon size={16} />,
  position_liquidated: '💥',
  price_alert: <BellIcon size={16} />,
  achievement: <TrophyIcon size={16} />,
  funding_payment: '💸',
};

// ── Relative time helper ────────────────────────────────────────
function relativeTime(ts: number): string {
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// ── Single notification row ─────────────────────────────────────
const NotifItem: React.FC<{ notif: AppNotification; onClick: (id: string) => void }> = ({ notif, onClick }) => (
  <div className={`notif-item ${!notif.read ? 'unread' : ''}`} onClick={() => onClick(notif.id)}>
    <div className="notif-item-icon">{TYPE_ICON[notif.type]}</div>
    <div className="notif-item-body">
      <div className="notif-item-title">{notif.title}</div>
      <div className="notif-item-desc">{notif.description}</div>
      <div className="notif-item-time">{relativeTime(notif.timestamp)}</div>
    </div>
    {!notif.read && <div className="notif-unread-dot" />}
  </div>
);

// ── Bell SVG (for notification center button) ──────────────────
const NotifBellIcon: React.FC = () => (
  <svg className="notif-bell-icon" viewBox="0 0 24 24">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

// ── Main component ──────────────────────────────────────────────
const NotificationCenter: React.FC = () => {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const { notifications, markRead, markAllRead, clearAll } = useNotificationStore();
  const unread = notifications.filter((n) => !n.read).length;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        btnRef.current && !btnRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleItemClick = useCallback((id: string) => {
    markRead(id);
  }, [markRead]);

  return (
    <div className="notif-wrapper">
      <button
        ref={btnRef}
        className="notif-bell-btn"
        onClick={() => setOpen((v) => !v)}
        title="Notifications"
        aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ''}`}
      >
        <NotifBellIcon />
        {unread > 0 && (
          <span className="notif-badge">{unread > 99 ? '99+' : unread}</span>
        )}
      </button>

      {open && (
        <div className="notif-panel" ref={panelRef}>
          <div className="notif-panel-header">
            <span className="notif-panel-title">Notifications</span>
            <div className="notif-panel-actions">
              {unread > 0 && (
                <button className="notif-action-btn" onClick={markAllRead}>
                  Mark all read
                </button>
              )}
              {notifications.length > 0 && (
                <button className="notif-action-btn" onClick={clearAll}>
                  Clear all
                </button>
              )}
            </div>
          </div>

          <div className="notif-list">
            {notifications.length === 0 ? (
              <div className="notif-empty">
                <div className="notif-empty-icon">🔔</div>
                No notifications yet
              </div>
            ) : (
              notifications.map((n) => (
                <NotifItem key={n.id} notif={n} onClick={handleItemClick} />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
